import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BORTransferRequestPostBody,
  BORTransferRequestSearchQuery,
  BORTransferRequestSearchResponse,
  BORTransferRequestGetResponse,
  BORTransferRequestCancelParams,
  BORTransferRequestCancelResponse,
} from '@lio/api-types';
import { useClient } from '@utils/api-client';
import { borTransferRequestQueryKeys } from './keys';

const buildSearchQueryParams = (params: BORTransferRequestSearchQuery): string => {
  const query = new URLSearchParams();

  const { pageOffset, pageLimit, sourceBrokerageId, sourceProducerId, targetBrokerageId, targetProducerId, status } =
    params;

  if (pageOffset !== undefined) {
    query.append('pageOffset', String(pageOffset));
  }

  if (pageLimit !== undefined) {
    query.append('pageLimit', String(pageLimit));
  }

  if (sourceBrokerageId !== undefined) {
    query.append('sourceBrokerageId', sourceBrokerageId);
  }

  if (sourceProducerId !== undefined) {
    query.append('sourceProducerId', sourceProducerId);
  }

  if (targetBrokerageId !== undefined) {
    query.append('targetBrokerageId', targetBrokerageId);
  }

  if (targetProducerId !== undefined) {
    query.append('targetProducerId', targetProducerId);
  }

  if (status !== undefined) {
    query.append('status', status);
  }

  return query.toString();
};

export const useSearchBORTransferRequests = (params: BORTransferRequestSearchQuery, enabled: boolean = true) => {
  const client = useClient();
  const queryString = buildSearchQueryParams(params);

  const result = useQuery<BORTransferRequestSearchResponse>({
    queryKey: borTransferRequestQueryKeys.search(params),
    queryFn: () => client.get(`bor-transfer-request?${queryString}`).json<BORTransferRequestSearchResponse>(),
    enabled,
  });

  return { ...result, borTransferRequests: result.data };
};

export const useGetBORTransferRequest = (id: string) => {
  const client = useClient();

  const result = useQuery<BORTransferRequestGetResponse>({
    queryKey: borTransferRequestQueryKeys.detail(id),
    queryFn: () => client.get(`bor-transfer-request/${id}`).json<BORTransferRequestGetResponse>(),
    enabled: !!id,
  });

  return { ...result, borTransferRequest: result.data };
};

export const useCreateBORTransferRequest = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  const result = useMutation({
    mutationFn: (body: BORTransferRequestPostBody) =>
      client
        .post('bor-transfer-request', {
          json: body,
        })
        .json<BORTransferRequestGetResponse>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: borTransferRequestQueryKeys.all });
    },
  });

  return { ...result, createBORTransferRequest: result.mutateAsync };
};

export const useCancelPolicyBORTransfer = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  const result = useMutation({
    mutationFn: ({ borTransferRequestId, policyId }: BORTransferRequestCancelParams) =>
      client
        .patch(`bor-transfer-request/${borTransferRequestId}/policy/${policyId}/cancel`)
        .json<BORTransferRequestCancelResponse>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: borTransferRequestQueryKeys.all });
    },
  });

  return { ...result, cancelPolicyBORTransfer: result.mutateAsync };
};
